package se.mittpass.web.components

import androidx.compose.runtime.Composable
import org.jetbrains.compose.web.dom.*
import org.jetbrains.compose.web.attributes.*
import se.mittpass.shared.api.SlotDto

@Composable
fun ApplicantNoticeBanner(
    slotsWithApplicants: List<SlotDto>,
    onReviewClick: () -> Unit,
    modifier: (org.jetbrains.compose.web.css.StyleScope.() -> Unit)? = null
) {
    if (slotsWithApplicants.isEmpty()) return

    val totalSlots = slotsWithApplicants.size
    val uniqueApplicants = slotsWithApplicants
        .flatMap { it.interestedUsers }
        .distinctBy { it.id }

    val totalApplicantCount = uniqueApplicants.size

    val nameSample = uniqueApplicants
        .take(3)
        .joinToString(", ") { user ->
            formatCleanDisplayName(user.email)
        }

    val titleText = if (totalSlots == 1) {
        "1 otilldelat pass har sökande personal"
    } else {
        "$totalSlots otilldelade pass har sökande personal"
    }

    val subtext = if (totalApplicantCount > 0) {
        val extra = if (totalApplicantCount > 3) " m.fl." else ""
        "$totalApplicantCount sökande medarbetare ($nameSample$extra) • Klicka för snabb granskning"
    } else {
        "Klicka för att granska sökande och tilldela pass"
    }

    Div({
        onClick { onReviewClick() }
        style {
            property("background", "var(--rp-surface)")
            property("border", "1px solid var(--rp-hl-med)")
            property("border-left", "4px solid var(--rp-pine)")
            property("border-radius", "16px")
            property("padding", "0.9rem 1.25rem")
            property("margin-bottom", "1rem")
            property("display", "flex")
            property("justify-content", "space-between")
            property("align-items", "center")
            property("gap", "1rem")
            property("flex-wrap", "wrap")
            property("cursor", "pointer")
            property("box-shadow", "0 2px 10px rgba(0,0,0,0.03)")
            property("transition", "all 0.15s ease")
            modifier?.invoke(this)
        }
    }) {
        // Left section: Icon badge + Title + Subtitle tags
        Div({
            style {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "0.9rem")
                property("min-width", "0")
            }
        }) {
            // Icon Badge
            Div({
                style {
                    property("width", "40px")
                    property("height", "40px")
                    property("border-radius", "12px")
                    property("background", "var(--rp-info-bg)")
                    property("color", "var(--rp-pine)")
                    property("display", "flex")
                    property("align-items", "center")
                    property("justify-content", "center")
                    property("flex-shrink", "0")
                }
            }) {
                FaIcon("fa-solid fa-user-check", size = "1.05rem", color = "var(--rp-pine)")
            }

            // Text Info & Applicants
            Div({ style { property("min-width", "0") } }) {
                Div({
                    style {
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.5rem")
                        property("flex-wrap", "wrap")
                    }
                }) {
                    Span({
                        style {
                            property("font-weight", "800")
                            property("color", "var(--rp-text)")
                            property("font-size", "0.92rem")
                            property("letter-spacing", "-0.2px")
                        }
                    }) {
                        Text(titleText)
                    }

                    // Slot Count Badge
                    Span({
                        style {
                            property("background", "var(--rp-info-bg)")
                            property("color", "var(--rp-iris)")
                            property("padding", "0.15rem 0.55rem")
                            property("border-radius", "50px")
                            property("font-size", "0.72rem")
                            property("font-weight", "800")
                        }
                    }) {
                        Text("$totalSlots pass")
                    }
                }

                Div({
                    style {
                        property("color", "var(--rp-subtle)")
                        property("font-size", "0.78rem")
                        property("margin-top", "0.2rem")
                        property("font-weight", "600")
                        property("white-space", "nowrap")
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("display", "flex")
                        property("align-items", "center")
                        property("gap", "0.4rem")
                    }
                }) {
                    FaIcon("fa-solid fa-users", size = "0.7rem", color = "var(--rp-muted)")
                    Text(subtext)
                }
            }
        }

        // Right CTA Button (Pill Action button with icon & chevron)
        Button({
            onClick { e ->
                e.stopPropagation()
                onReviewClick()
            }
            style {
                property("padding", "0.55rem 1.15rem")
                property("border", "none")
                property("background", "var(--rp-pine)")
                property("color", "#FFFFFF")
                property("border-radius", "50px")
                property("font-weight", "800")
                property("font-size", "0.82rem")
                property("cursor", "pointer")
                property("white-space", "nowrap")
                property("font-family", "inherit")
                property("box-shadow", "0 2px 8px rgba(79,70,229,0.25)")
                property("flex-shrink", "0")
                property("display", "flex")
                property("align-items", "center")
                property("gap", "0.5rem")
                property("transition", "all 0.15s ease")
            }
        }) {
            FaIcon("fa-solid fa-clipboard-check", size = "0.82rem", color = "var(--rp-surface)")
            Text("Granska & Tilldela")
            FaIcon("fa-solid fa-arrow-right", size = "0.72rem", color = "var(--rp-surface)")
        }
    }
}

private fun formatCleanDisplayName(email: String): String {
    val prefix = email.substringBefore("@")
    val parts = prefix.split(".", "_", "-")
    return if (parts.size >= 2) {
        val firstName = parts[0].replaceFirstChar { it.uppercase() }
        val lastInitial = parts[1].firstOrNull()?.uppercaseChar()?.let { " $it." } ?: ""
        "$firstName$lastInitial"
    } else {
        prefix.replaceFirstChar { it.uppercase() }
    }
}
